"use client";

import React from "react";
import ProblemAnswer from "@/components/ProblemAnswer";
import { useProblemData } from "@/components/ProblemDataContext";
import { useProblemQuery } from "@/hooks/useProblemQuery";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useProblemCacheState } from "@/components/ProblemCacheInvalidator";

import type { ProblemAnswerSessionState } from "@workspace/gamja-platform/lib/random-problems-persistence";

import { isDraftCompatible } from "@workspace/gamja-platform/lib/problem-answer-draft-compatibility";

interface ProblemAnswerFetcherProps {
  cardHeader?: React.ReactNode;
  cardFooter?: React.ReactNode;
  readOnlySession?: boolean;
  disableHistory?: boolean;
  previewSolutionInShell?: boolean;
  initialSessionState?: ProblemAnswerSessionState;
  onSessionStateChange?: (state: ProblemAnswerSessionState) => void;
  id: string;
  choiceCopy?: boolean;
  alwaysShowChoices?: boolean;
  allowQuotedSqlAlias?: boolean;
  defaultErrorNoteOpen?: boolean;
  notRecommended?: boolean;
  notRecommendedReason?: string;
  defaultSolutionOpen?: boolean;
  problemType?: "기초" | "비추천" | "기출변형" | "기출";
  problemTypes?: ("기초" | "비추천" | "기출변형" | "기출")[];
  variationNote?: string;
  gridCols?: 2 | 3 | 4;
  premium?: boolean;
  problemNumber?: number;
  totalProblems?: number;
  resolveNoteSession?: (createdAtMs: number) => string | null;
  // 입력 중인 답이 확대/레이아웃 전환으로 인한 remount에서 사라지지 않도록 기본 보존한다.
  persistDraft?: boolean;
  persistDraftAcrossReload?: boolean;
  revealWithoutGradingNotice?: string;
}

const ProblemAnswerFetcher: React.FC<ProblemAnswerFetcherProps> = ({
  id,
  cardHeader,
  cardFooter,
  readOnlySession = false,
  disableHistory,
  previewSolutionInShell,
  initialSessionState,
  onSessionStateChange,
  choiceCopy,
  alwaysShowChoices,
  allowQuotedSqlAlias,
  defaultErrorNoteOpen,
  notRecommended,
  notRecommendedReason,
  defaultSolutionOpen,
  problemType,
  problemTypes,
  variationNote,
  gridCols,
  premium,
  problemNumber,
  totalProblems,
  resolveNoteSession,
  persistDraft = true,
  persistDraftAcrossReload,
  revealWithoutGradingNotice,
}) => {
  // Context에서 배치 조회된 문제 데이터 (있으면 우선 사용)
  const { problems: contextProblems, isLoading: isLoadingFromContext } =
    useProblemData();
  const cacheState = useProblemCacheState();

  // React Query로 개별 조회 (Context에 없을 때 fallback)
  const contextData = contextProblems[id];
  const hasContextData = contextData !== undefined && contextData !== null;
  const {
    data: queryData,
    isLoading: isQueryLoading,
    isFetching,
    isPaused,
    error: queryError,
    refetch,
  } = useProblemQuery(id, !hasContextData);

  // Context 데이터 우선, 없으면 Query 데이터 사용
  const problemData = hasContextData ? contextData : queryData;
  // 전체 문제 배치 조회가 끝난 뒤 본문을 표시한다.
  const isLoading =
    isLoadingFromContext ||
    (!problemData &&
      !isPaused &&
      (isQueryLoading || (!queryError && cacheState === "checking")));
  const error = !problemData
    ? isPaused
      ? "인터넷 연결을 확인해 주세요. 연결되면 문제를 다시 불러와요."
      : (queryError?.message ?? "문제를 불러오지 못했어요. 다시 시도해 주세요.")
    : null;

  if (isLoading) {
    // 로딩 스켈레톤에도 problem-{id} 앵커를 달아, 데이터 로드 전이라도 "문제" 점프
    // 버튼이 첫 문제 위치를 정확히 찾도록 한다(JCG-2519: 모바일에서 첫 문제가 아직
    // 로딩 중이면 먼저 로드된 중간 문제로 점프하던 문제 방지).
    return (
      <div
        id={`problem-${id}`}
        className="space-y-3"
        role="status"
        aria-busy="true"
      >
        <span className="sr-only">문제를 불러오는 중이에요.</span>
        <Skeleton
          className="h-6 motion-reduce:animate-none"
          aria-hidden="true"
        />
        <Skeleton
          className="h-4 motion-reduce:animate-none"
          aria-hidden="true"
        />
        <Skeleton
          className="h-4 w-5/6 motion-reduce:animate-none"
          aria-hidden="true"
        />
        <div className="flex gap-2" aria-hidden="true">
          <Skeleton className="h-8 w-16 motion-reduce:animate-none" />
          <Skeleton className="h-8 w-16 motion-reduce:animate-none" />
          <Skeleton className="h-8 w-16 motion-reduce:animate-none" />
          <Skeleton className="h-8 w-16 motion-reduce:animate-none" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        id={`problem-${id}`}
        role="alert"
        className="my-10 overflow-hidden rounded-lg border border-red-200 bg-red-50 dark:border-red-700 dark:bg-red-900/20"
      >
        <div className="p-6 text-center">
          <p className="text-red-600 dark:text-red-400 font-medium">오류</p>
          <p className="text-red-700 dark:text-red-300 mt-2 break-words">
            {error}
          </p>
          <Button
            type="button"
            variant="outline"
            className="mt-4"
            onClick={() => void refetch()}
            disabled={isFetching || isPaused}
          >
            문제 다시 불러오기
          </Button>
        </div>
      </div>
    );
  }

  if (!problemData) {
    return null;
  }

  const lockAnswer = readOnlySession && (
    !initialSessionState?.isCorrectAnswerVisible ||
    !isDraftCompatible(initialSessionState.answer, problemData.correctAnswer)
  );

  return (
    <fieldset disabled={lockAnswer} className="min-w-0">
      <ProblemAnswer
        cardHeader={cardHeader}
        cardFooter={cardFooter}
        id={id}
        disableHistory={disableHistory}
        previewSolutionInShell={previewSolutionInShell}
        initialSessionState={initialSessionState}
        onSessionStateChange={onSessionStateChange}
        problem={problemData.problem}
        correctAnswer={problemData.correctAnswer}
        examSessions={problemData.examSessions}
        choices={problemData.choices}
        solution={problemData.solution}
        shuffle={problemData.shuffle ?? true}
        ignoreOrder={problemData.ignoreOrder ?? false}
        choiceCopy={choiceCopy ?? problemData.choiceCopy ?? false}
        alwaysShowChoices={
          alwaysShowChoices ?? problemData.alwaysShowChoices ?? false
        }
        strictWhitespace={problemData.strictWhitespace ?? false}
        caseSensitive={problemData.caseSensitive ?? false}
        defaultErrorNoteOpen={defaultErrorNoteOpen}
        notRecommended={notRecommended ?? problemData.notRecommended ?? false}
        notRecommendedReason={
          notRecommendedReason ?? problemData.notRecommendedReason
        }
        defaultSolutionOpen={defaultSolutionOpen}
        problemType={problemType ?? problemData.problemType}
        problemTypes={problemTypes ?? problemData.problemTypes}
        variationNote={variationNote ?? problemData.variationNote}
        gridCols={gridCols ?? problemData.gridCols}
        premium={premium ?? problemData.premium ?? false}
        hiddenCorrectAnswers={problemData.hiddenCorrectAnswers}
        compareAsSet={problemData.compareAsSet ?? false}
        allowQuotedSqlAlias={
          allowQuotedSqlAlias ?? problemData.allowQuotedSqlAlias ?? false
        }
        tags={problemData.tags}
        answerSeparator={problemData.answerSeparator}
        relatedTheory={problemData.relatedTheory}
        problemNumber={problemNumber}
        totalProblems={totalProblems}
        resolveNoteSession={resolveNoteSession}
        persistDraft={persistDraft}
        persistDraftAcrossReload={persistDraftAcrossReload}
        revealWithoutGradingNotice={revealWithoutGradingNotice}
      />
    </fieldset>
  );
};

export default ProblemAnswerFetcher;
